import { Download, Share, Plus, X } from "lucide-react";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { toast } from "sonner";

/**
 * InstallButton — shows a button only when the PWA is actually installable.
 * Hides itself entirely when the app is already running in standalone mode.
 */
export function InstallButton({ variant = "solid", className = "", label = "Installa App" }) {
  const { isAvailable, isIOS, isStandalone, promptInstall } = usePWAInstall();

  if (!isAvailable || isStandalone) return null;

  const handleClick = async () => {
    const result = await promptInstall();
    if (result === "accepted") {
      toast.success("App installata correttamente!");
    } else if (result === "unavailable") {
      toast.message("Apri l'app dal menu del browser per installarla.");
    }
  };

  const base =
    "inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-mono text-[11px] uppercase tracking-[0.22em] transition active:scale-[0.98]";
  const styles =
    variant === "solid"
      ? "bg-[#FF6B00] text-[#12100e] border border-[#FF6B00] hover:bg-[#ff8226] shadow-[0_0_20px_rgba(255,107,0,0.4)]"
      : "border border-[#FF6B00]/40 text-[#FF6B00] hover:bg-[#FF6B00]/10";

  return (
    <button
      data-testid="install-pwa-button"
      onClick={handleClick}
      className={`${base} ${styles} ${className}`}
    >
      <Download size={14} />
      {label}
      {isIOS && (
        <span className="ml-1 text-[9px] opacity-80">(iOS)</span>
      )}
    </button>
  );
}

/**
 * IOSInstallModal — full instructions overlay for iOS Safari users
 * who must manually use Share → Add to Home Screen.
 */
export function IOSInstallModal() {
  const { showIOSModal, setShowIOSModal } = usePWAInstall();
  if (!showIOSModal) return null;

  return (
    <div
      data-testid="ios-install-modal"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={() => setShowIOSModal(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-[#1a1816] border border-[#FF6B00]/40 rounded-2xl p-6 shadow-[0_30px_60px_rgba(0,0,0,0.8)]"
      >
        <button
          onClick={() => setShowIOSModal(false)}
          aria-label="Chiudi"
          data-testid="ios-modal-close"
          className="absolute top-3 right-3 p-1.5 rounded-md text-[#A39A8F] hover:text-[#F5F2EB] hover:bg-[#332f2b] transition"
        >
          <X size={18} />
        </button>

        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF6B00] mb-3">
          Installa su iPhone / iPad
        </div>
        <h3 className="font-anton uppercase text-2xl text-[#F5F2EB] leading-tight mb-5">
          Aggiungi Rumore Zero alla{" "}
          <span className="text-[#FF6B00]">schermata Home</span>
        </h3>

        <ol className="space-y-4 font-mono text-sm text-[#A39A8F]">
          <li className="flex items-start gap-3">
            <span className="shrink-0 w-7 h-7 rounded-full bg-[#FF6B00] text-[#12100e] font-anton flex items-center justify-center text-sm">
              1
            </span>
            <span className="leading-relaxed pt-0.5">
              Tocca il pulsante <Share size={14} className="inline align-text-bottom mx-1 text-[#FF6B00]" /><span className="text-[#F5F2EB]">Condividi</span> nella barra di Safari.
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="shrink-0 w-7 h-7 rounded-full bg-[#FF6B00] text-[#12100e] font-anton flex items-center justify-center text-sm">
              2
            </span>
            <span className="leading-relaxed pt-0.5">
              Scorri e seleziona <span className="text-[#F5F2EB]">Aggiungi a Home</span>{" "}
              <Plus size={14} className="inline align-text-bottom mx-1 text-[#FF6B00]" />.
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="shrink-0 w-7 h-7 rounded-full bg-[#FF6B00] text-[#12100e] font-anton flex items-center justify-center text-sm">
              3
            </span>
            <span className="leading-relaxed pt-0.5">
              Conferma con <span className="text-[#F5F2EB]">Aggiungi</span>. L'icona di Rumore Zero apparirà sulla tua Home.
            </span>
          </li>
        </ol>

        <div className="mt-6 p-3 rounded-lg border border-[#332f2b] bg-[#0d0b0a] font-mono text-[11px] text-[#69635c] leading-relaxed">
          Su iOS la funzione è disponibile solo dal browser <span className="text-[#E1A140]">Safari</span> (non da Chrome o altri browser).
        </div>
      </div>
    </div>
  );
}
